import React, { useEffect, useRef, useState } from 'react';
import { Title } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { apiGet } from '../services/apiClient';

const initialFilters = { platform: '', userId: '', code: '', requestId: '', since: '', until: '' };
const dateText = (value) => (value ? new Date(value).toLocaleString() : '—');

export default function SystemErrorsPage() {
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState({ data: [], total: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const detailGeneration = useRef(0);
  useEffect(
    () => () => {
      detailGeneration.current++;
    },
    [],
  );
  useEffect(() => {
    let active = true;
    const query = new URLSearchParams({ page: String(page), perPage: '25' });
    Object.entries(filters).forEach(([key, value]) => {
      if (value)
        query.set(
          key,
          key === 'since' || key === 'until' ? new Date(value).toISOString() : value.trim(),
        );
    });
    setBusy(true);
    setError('');
    apiGet('system-errors?' + query.toString())
      .then((data) => {
        if (active) setResult(data);
      })
      .catch((err) => {
        if (active) setError(err.message || 'Hata kayıtları yüklenemedi');
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [filters, page, revision]);

  const field = (name, label, type = 'text') => (
    <TextField
      size="small"
      label={label}
      type={type}
      value={draft[name]}
      InputLabelProps={type === 'datetime-local' ? { shrink: true } : undefined}
      onChange={(event) => setDraft({ ...draft, [name]: event.target.value })}
    />
  );
  const openDetail = async (id) => {
    const current = ++detailGeneration.current;
    setDetail({ loading: true });
    try {
      const data = await apiGet('system-errors/' + encodeURIComponent(id));
      if (current === detailGeneration.current) setDetail(data);
    } catch {
      if (current === detailGeneration.current) setDetail({ failed: true });
    }
  };
  const closeDetail = () => {
    detailGeneration.current++;
    setDetail(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Sistem Hataları" />
      <Typography variant="h5" sx={{ mb: 2 }}>
        Sistem Hataları
      </Typography>
      <Typography sx={{ mb: 2 }} color="text.secondary">
        Panel, web, mobil ve API hataları. Varsayılan görünüm son 7 günü gösterir.
      </Typography>
      <Box
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setFilters({ ...draft });
        }}
      >
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Platform"
            value={draft.platform}
            sx={{ minWidth: 130 }}
            onChange={(event) => setDraft({ ...draft, platform: event.target.value })}
          >
            <MenuItem value="">Tümü</MenuItem>
            {['api', 'panel', 'web', 'mobile'].map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
          {field('userId', 'Kullanıcı ID')}
          {field('code', 'Hata kodu')}
          {field('requestId', 'Takip numarası')}
          {field('since', 'Başlangıç', 'datetime-local')}
          {field('until', 'Bitiş', 'datetime-local')}
          <Button type="submit" variant="contained" disabled={busy}>
            Filtrele
          </Button>
          <Button onClick={() => setRevision((value) => value + 1)} disabled={busy}>
            Yenile
          </Button>
        </Stack>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {busy && <Typography role="status">Yükleniyor…</Typography>}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                'Son görülme',
                'Platform / sürüm',
                'Kullanıcı',
                'Hata',
                'Sayfa / işlem',
                'Tekrar',
                '',
              ].map((label) => (
                <TableCell key={label}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {result.data.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{dateText(row.lastSeen)}</TableCell>
                <TableCell>
                  {row.platform} / {row.version}
                  <br />
                  {row.source}
                </TableCell>
                <TableCell>{row.userId || 'Misafir / sistem'}</TableCell>
                <TableCell sx={{ maxWidth: 380, overflowWrap: 'anywhere' }}>
                  <strong>{row.code}</strong>
                  <br />
                  {row.message}
                </TableCell>
                <TableCell>
                  {row.route}
                  <br />
                  {row.operation}
                </TableCell>
                <TableCell>{row.occurrences}</TableCell>
                <TableCell>
                  <Button onClick={() => openDetail(row.id)}>Ayrıntı</Button>
                </TableCell>
              </TableRow>
            ))}
            {!busy && !error && result.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>Bu filtrelerle hata kaydı bulunamadı.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Button disabled={page <= 1 || busy} onClick={() => setPage(page - 1)}>
          Önceki
        </Button>
        <Typography>
          {page}. sayfa · {result.total} kayıt
        </Typography>
        <Button
          disabled={busy || page >= 200 || page * 25 >= result.total}
          onClick={() => setPage(page + 1)}
        >
          Sonraki
        </Button>
      </Stack>
      <Dialog open={!!detail} onClose={closeDetail} fullWidth maxWidth="md">
        <DialogTitle>Hata ayrıntısı</DialogTitle>
        <DialogContent>
          {detail?.loading ? (
            <Typography>Yükleniyor…</Typography>
          ) : detail?.failed ? (
            <Alert severity="error">Kayıt yüklenemedi.</Alert>
          ) : (
            <Box
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13 }}
            >
              {JSON.stringify(detail, null, 2)}
            </Box>
          )}
          <Button onClick={closeDetail}>Kapat</Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
